import { FunctionComponent, useEffect, useState } from "react"
import { Alert, Button, Col, Form, Row } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate, useParams } from "react-router-dom"
import ItemSelect from "../components/ItemSelect"
import { AccessController, RentManagerProperty } from "../data/models.interface"
import { useCreateAccessControllerQuery } from "../hooks/queries/useCreateAccessControllerQuery"
import { useGetAccessControllersQuery } from "../hooks/queries/useGetAccessControllersQuery"
import { useGetRentManagerPropertiesQuery } from "../hooks/queries/useGetRentManagerPropertiesQuery"
import { useUpdateAccessControllerQuery } from "../hooks/queries/useUpdateAccessControllerQuery"
import { Parameters } from "../hooks/useLazyQuery"

interface IAccessControllerPage {}
const AccessControllerPage: FunctionComponent<IAccessControllerPage> = () => {
	const params = useParams()
	const accessControllerId = params.accessControllerId
	//
	const accessControllersQuery = useGetAccessControllersQuery()
	const controllerLoaded = accessControllersQuery.data?.accessControllers?.find((s) => s.id === accessControllerId)
	const createControllerQuery = useCreateAccessControllerQuery()
	const updateControllerQuery = useUpdateAccessControllerQuery(accessControllerId || "error")
	const saveQuery = accessControllerId === "new" ? createControllerQuery : updateControllerQuery
	const propertiesQuery = useGetRentManagerPropertiesQuery()
	//
	const [accessController, setAccessController] = useState<AccessController>()
	const [touched, setTouched] = useState(new Map<string, boolean>())
	const [validationErrors, setValidationErrors] = useState(new Map<string, string>())
	const [makeMissingDoors, setMakeMissingDoors] = useState(false)
	//
	useEffect(() => {
		// only init state once
		if (accessController == null) {
			if (accessControllerId === "new") {
				setAccessController(controllerTemplate)
				setMakeMissingDoors(true)
			} else if (controllerLoaded) setAccessController(controllerLoaded)
		}
	}, [accessControllerId, controllerLoaded, accessController])
	// error message
	const invalidIdError =
		accessControllerId !== "new" && accessControllersQuery.data != null && controllerLoaded == null
			? "No Access Controller found"
			: undefined
	const error =
		createControllerQuery.error || updateControllerQuery.error || accessControllersQuery.error || invalidIdError
	// loading layout
	const loading = !propertiesQuery.data || !accessController
	if (loading) return <LoadingLayout error={error} />
	// normal layout
	return (
		<Container>
			<h2 className="text-center mt-4">Access Controller</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<AccessControllerForm
				accessController={accessController}
				setAccessController={setAccessController}
				touched={touched}
				setTouched={setTouched}
				validationErrors={validationErrors}
				setValidationErrors={setValidationErrors}
				properties={propertiesQuery.data?.rentManagerProperties!}
			/>
			<Row>
				<Col></Col>
				<Col xs="auto">
					<Form.Check
						className="mb-3"
						id="makeMissingDoors"
						label="Make Missing Doors"
						checked={makeMissingDoors}
						onChange={(e) => setMakeMissingDoors(e.target.checked)}
					/>
				</Col>
			</Row>
			<Row>
				<Col xs="auto">
					<BackButton />
				</Col>
				<Col></Col>
				<Col xs="auto">
					<SaveButton
						accessController={accessController}
						setTouched={setTouched}
						validationErrors={validationErrors}
						makeMissingDoors={makeMissingDoors}
						query={saveQuery.query}
						loading={saveQuery.loading}
					/>
				</Col>
			</Row>
		</Container>
	)
}
export default AccessControllerPage

const LoadingLayout = ({ error }: { error?: string }) => {
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Access Controller</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<div className="text-center">Loading</div>
		</Container>
	)
}

interface IAccessControllerForm {
	accessController: AccessController
	setAccessController: (accessController: AccessController) => void
	touched: Map<string, boolean>
	setTouched: (value: Map<string, boolean>) => void
	validationErrors: Map<string, string>
	setValidationErrors: (e: Map<string, string>) => void
	properties: RentManagerProperty[]
}
const AccessControllerForm = ({
	accessController,
	setAccessController,
	touched,
	setTouched,
	validationErrors,
	setValidationErrors,
	properties,
}: IAccessControllerForm) => {
	const setFieldTouched = (field: string) => {
		// need to make it a new object to trigger the state change
		touched = new Map(touched)
		touched.set(field, true)
		setTouched(touched)
	}
	const setSerialNumber = (value: string) => {
		setAccessController({ ...accessController, serialNumber: value })
		//
		validationErrors = new Map(validationErrors)
		if (!/^\d+$/.test(value)) validationErrors.set("serialNumber", "Serial Number must be only numbers.")
		else if (value.length !== 9) validationErrors.set("serialNumber", "Serial Number must be 9 digits.")
		else validationErrors.delete("serialNumber")
		setValidationErrors(validationErrors)
	}
	const setIpAddress = (value: string) => {
		setAccessController({ ...accessController, ipAddress: value })
		//
		validationErrors = new Map(validationErrors)
		const ipv4Regex =
			/^(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
		if (!ipv4Regex.test(value)) validationErrors.set("ipAddress", "IP Address must be a valid IPv4 address.")
		else validationErrors.delete("ipAddress")
		setValidationErrors(validationErrors)
	}
	const setPort = (value: string) => {
		const port = Number(value.replace(/[^0-9]/g, ""))
		setAccessController({ ...accessController, port: port })
		//
		validationErrors = new Map(validationErrors)
		if (port < 0 || port > 65535) validationErrors.set("port", "Port must be between 0 and 65635.")
		else validationErrors.delete("port")
		setValidationErrors(validationErrors)
	}
	const setProperty = (p?: RentManagerProperty) =>
		setAccessController({
			...accessController,
			propertyId: p?.PropertyID!,
			propertyName: p?.Name!,
			propertyShortName: p?.ShortName!,
		})
	const setActive = (value: boolean) => setAccessController({ ...accessController, active: value })
	return (
		<>
			<Row className="mt-4">
				<Col xs={12} sm={6} lg>
					<Form.Group className="mb-3" controlId="serialNumber">
						<Form.Label>Serial Number</Form.Label>
						<Form.Control
							type="text"
							required
							value={accessController.serialNumber || ""}
							onChange={(e) => setSerialNumber(e.target.value)}
							onBlur={() => setFieldTouched("serialNumber")}
							isInvalid={touched.get("serialNumber") && validationErrors.has("serialNumber")}
						/>
						<Form.Control.Feedback type="invalid">{validationErrors.get("serialNumber")}</Form.Control.Feedback>
					</Form.Group>
				</Col>
				<Col xs={12} sm={6} lg>
					<Form.Group className="mb-3" controlId="ipAddress">
						<Form.Label>IP Address</Form.Label>
						<Form.Control
							type="text"
							required
							value={accessController.ipAddress || ""}
							onChange={(e) => setIpAddress(e.target.value)}
							onBlur={() => setFieldTouched("ipAddress")}
							isInvalid={touched.get("ipAddress") && validationErrors.has("ipAddress")}
						/>
						<Form.Control.Feedback type="invalid">{validationErrors.get("ipAddress")}</Form.Control.Feedback>
					</Form.Group>
				</Col>
				<Col xs={6} sm={6} lg>
					<Form.Group className="mb-3" controlId="port">
						<Form.Label>Port</Form.Label>
						<Form.Control
							type="number"
							required
							value={accessController.port || ""}
							onChange={(e) => setPort(e.target.value)}
							onBlur={() => setFieldTouched("port")}
							isInvalid={touched.get("port") && validationErrors.has("port")}
						/>
						<Form.Control.Feedback type="invalid">{validationErrors.get("port")}</Form.Control.Feedback>
					</Form.Group>
				</Col>
				<Col xs={12} sm={6} lg={4}>
					<Form.Group className="mb-3" controlId="property">
						<Form.Label>Property</Form.Label>
						<ItemSelect
							placeholder="--Select a Property--"
							required
							items={properties}
							convert={(p) => ({ id: p.PropertyID.toFixed(0), name: p.Name })}
							valueId={accessController.propertyId?.toFixed(0)}
							onChange={setProperty}
						/>
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="enabled">
						<Form.Label>Active</Form.Label>
						<Form.Select
							required
							value={accessController.active ? "true" : "false"}
							onChange={(e) => setActive(e.target.value === "true")}>
							<option value="true">Enabled</option>
							<option value="false">Disabled</option>
						</Form.Select>
					</Form.Group>
				</Col>
			</Row>
		</>
	)
}

const BackButton = () => {
	const navigate = useNavigate()
	return (
		<Button variant="secondary" className="mb-4" onClick={() => navigate(-1)}>
			Back
		</Button>
	)
}

interface ISaveButton {
	accessController: AccessController
	setTouched: (value: Map<string, boolean>) => void
	validationErrors: Map<string, string>
	makeMissingDoors: boolean
	query: (accessController: Parameters<AccessController>) => Promise<void>
	loading: boolean
}
const SaveButton = ({
	accessController,
	setTouched,
	validationErrors,
	makeMissingDoors,
	query,
	loading,
}: ISaveButton) => {
	const navigate = useNavigate()
	const save = async () => {
		// show errors on all fields
		const touchMap = new Map<string, boolean>()
		touchMap.set("serialNumber", true)
		touchMap.set("ipAddress", true)
		touchMap.set("port", true)
		setTouched(touchMap)
		//
		try {
			const errorCount = Array.from(validationErrors).filter((e) => e?.length > 0).length
			if (errorCount === 0) {
				accessController.makeMissingDoors = makeMissingDoors
				await query({ body: accessController })
				navigate(-1)
			}
		} catch (e) {
			return
		}
	}
	return (
		<Button variant="primary" className="mb-4" disabled={loading} onClick={save}>
			Save
		</Button>
	)
}

const controllerTemplate = {
	port: 60000,
	active: true,
} as AccessController
